import { useState } from 'react'
import { Eye, Monitor, Radio } from 'lucide-react'
import { Badge, Button, Drawer, IconButton, useToast } from '@/components/play'
import { useEventLiveKitLivePublisherIdentities } from '@/components/livekit/EventLiveKitProvider'
import { downloadObsSceneCollection } from '@/lib/obs'
import type { StreamResource } from '@/types/events'

interface DestinationsSheetProps {
  eventName: string
  streams: StreamResource[]
  /** False when the event's public page is turned off (`template_key === 'none'`). Defaults to on. */
  publicPageEnabled?: boolean
  /**
   * In-app path to the public page: `publicEventPath(event)`, the event's watch screen. Required, so a new caller
   * can't link a wrong page by leaving it out.
   */
  publicPath: string
}

type RowStatus =
  /** Live video is reaching the destination. Only real media evidence earns it. */
  | { kind: 'live' }
  /** Reachable, without live video. */
  | { kind: 'open' }
  | { kind: 'ready'; label: string }
  | { kind: 'idle'; label: string }

interface DestinationRowProps {
  icon: React.ReactNode
  title: string
  description: string
  status: RowStatus
  onAction?: () => void
  actionLabel?: string
  disabled?: boolean
}

function DestinationRow({
  icon,
  title,
  description,
  status,
  onAction,
  actionLabel,
  disabled,
}: DestinationRowProps) {
  return (
    <div className="flex items-center gap-s rounded-2xl bg-neutral-2 p-s">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-neutral-3 text-neutral-10" aria-hidden="true">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-neutral-10">{title}</div>
        <div className="truncate text-xs text-neutral-7">{description}</div>
      </div>
      {/* Green is reserved for live designation: an open page without live video stays neutral. */}
      {status.kind === 'live' && <Badge tone="live">Live</Badge>}
      {status.kind === 'open' && <Badge tone="neutral">Open</Badge>}
      {status.kind === 'ready' && <Badge tone="neutral">{status.label}</Badge>}
      {status.kind === 'idle' && <Badge tone="quiet">{status.label}</Badge>}
      {onAction && actionLabel && (
        <Button onClick={onAction} disabled={disabled} size="compact" variant="secondary">
          {actionLabel}
        </Button>
      )}
    </div>
  )
}

/**
 * Toolbar button + sheet that consolidates every output the event has: the public event page and the OBS Studio scene
 * collection. It lists only what works today.
 *
 * The badge on the toolbar button counts active destinations so users always
 * see "where this event is going" at a glance.
 *
 * The public page reads Live only while a public stream's LiveKit participant is
 * publishing video, from the surrounding `EventLiveKitProvider`. A stream record,
 * even an active one, never proves live video: without that evidence (or outside
 * a provider) an open page reads Open.
 */
export function DestinationsSheet({
  eventName,
  streams,
  publicPageEnabled = true,
  publicPath,
}: DestinationsSheetProps) {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()
  const liveIdentities = useEventLiveKitLivePublisherIdentities()
  const hasStreams = streams.length > 0
  const publicVideo = streams.some((stream) => {
    const identity = stream.attributes.participant_identity
    return stream.attributes.publicly_visible !== false && !!identity && liveIdentities.has(identity)
  })
  const eventPageOpen = publicPageEnabled && hasStreams // the public page opens with the first camera
  const obsReady = hasStreams // OBS export needs at least one camera
  const activeCount = (eventPageOpen ? 1 : 0) + (obsReady ? 1 : 0)

  function handleObsDownload() {
    const obsStreams = streams
      .map((s) => ({
        name: s.attributes.name,
        egressUrl: s.attributes.connection_details?.external_egress_url || '',
      }))
      .filter((s) => s.egressUrl)

    if (obsStreams.length === 0) {
      toast({
        title: 'No camera addresses yet',
        description: 'Your cameras’ OBS addresses appear once they connect. Try again then.',
        tone: 'error',
      })
      return
    }

    downloadObsSceneCollection(eventName, obsStreams)
    toast({
      title: 'OBS scenes downloaded',
      description: `A scene for each of ${obsStreams.length} camera${obsStreams.length === 1 ? '' : 's'}. Import it in OBS.`,
      tone: 'success',
    })
  }

  return (
    <Drawer
      open={open}
      onOpenChange={setOpen}
      trigger={
        <IconButton
          label={activeCount > 0 ? `Destinations, ${activeCount} ready` : 'Destinations'}
          variant="ghost"
          size="compact"
        >
          <>
            <Radio />
            {activeCount > 0 && (
              // The corner count of Play's icon-only tabs: an ink pill ringed in white. Its number is in the name.
              <span
                aria-hidden="true"
                className="absolute -right-xxs -top-xxs grid h-4 min-w-4 place-items-center rounded-full bg-neutral-10 px-xxs text-size-xs font-medium text-neutral-1 ring-2 ring-neutral-1"
              >
                {activeCount}
              </span>
            )}
          </>
        </IconButton>
      }
      eyebrow="Destinations"
      title="Where this event goes."
    >
      <div className="flex flex-col gap-xs">
        <DestinationRow
          icon={<Eye size={18} />}
          title="Public event page"
          // The address on this app's own host (a preview, staging or production), as Open will go there.
          description={`${window.location.host}${publicPath}`}
          status={
            !publicPageEnabled
              ? { kind: 'idle', label: 'Off' }
              : !eventPageOpen
              ? { kind: 'idle', label: 'Waiting for cameras' }
              : publicVideo
                ? { kind: 'live' }
                : { kind: 'open' }
          }
          onAction={publicPageEnabled ? () => window.open(publicPath, '_blank', 'noreferrer') : undefined}
          actionLabel="Open"
        />

        <DestinationRow
          icon={<Monitor size={18} />}
          title="OBS Studio"
          description={
            obsReady
              ? `SRT · ${streams.length} camera${streams.length === 1 ? '' : 's'}`
              : 'Add a camera for its OBS scene'
          }
          status={obsReady ? { kind: 'ready', label: 'Ready' } : { kind: 'idle', label: 'Waiting' }}
          onAction={handleObsDownload}
          actionLabel="Download"
          disabled={!obsReady}
        />
      </div>
    </Drawer>
  )
}
